import * as React from "react";
import { useQuery } from "../../Components/Query";
import { MatchSortHeader } from "../../Components/MatchSortHeader";
import { Column, Table } from "../../Components/QueryTable";
import { DateMatchSortHeader } from "../../Components/DateMatchSortHeader";
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { UserCreation, Study, Role } from "webportalclient";
import { RequestAcceptanceModal } from "./AssignRolesModal";
import { useSecurityContext } from "../../Core/SecurityContext";
import { ApiGetCountries } from "../../misc/api/AccountServer/AccountApi";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { HasPermissionMultiStudies, HasGlobalPermission, GlobalAuthorization } from "../../misc/components/Authorization";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { uniqBy } from "../../misc/ArrayFunctions";
import { VerifyAccessRequestsModal } from "./VerifyAccessRequestsModal";
import { AccessRequest } from "webportalclient";

export const AccessRequestList = () => {
    const [selectedRequest, setSelectedRequest] = React.useState<AccessRequest>(null);
    const [emailOptions, setEmailOptions] = React.useState([]);
    const [nameOptions, setNameOptions] = React.useState([]);
    const [organizationOptions, setOrganizationOptions] = React.useState([]);
    const [countryOptions, setCountryOptions] = React.useState([]);
    const [countries, setCountries] = React.useState([]);
    const [roleOptions, setRoleOptions] = React.useState([]);
    const [isFetchingCountries, setIsFetchingCountries] = React.useState(false);
    const [isFetchingFilterOptions, setIsFetchingFilterOptions] = React.useState(false);
    const [isFetchingModalOptions, setIsFetchingModalOptions] = React.useState(false);
    const [isRequestAcceptanceModalVisible, setIsRequestAcceptanceModalVisible] = React.useState(false);
    const [decisionButtonsDisabled, setDecisionButtonsDisabled] = React.useState(false);
    const [verifyVisible, setVerifyVisible] = React.useState<boolean>(false);
    const [errorAlert, setErrorAlert] = React.useState();
    const client = useWebPortalApiClient();
    const { refresh, data, applyFilters } = useQuery();
    const { currentUser, currentUserPermissions, roles } = useSecurityContext();

    const decisionButtonsContainerStyles: React.CSSProperties = { textAlign: "center" };
    const decisionButtonsStyles: React.CSSProperties = { margin: "0 3px" };

    React.useEffect(() => {
        const getCountries = async () => {
            setIsFetchingCountries(true);
            const accountCountries = await ApiGetCountries();
            setCountries(accountCountries.data);
            setIsFetchingCountries(false);
        }

        getCountries();
    }, []);

    React.useEffect(() => {
        const getFilterOptions = async () => {
            setIsFetchingFilterOptions(true);
            const requests = await client.client.getAccessRequests();
            if (requests.value?.length > 0) {
                const requestsEmails = requests.value.map(v => ({ label: v.email, value: v.email })).sort();
                setEmailOptions(requestsEmails);

                const requestsNames = requests.value.map(v => ({ label: v.fullName, value: v.fullName })).sort();
                setNameOptions(requestsNames);

                const requestsOrganizations = requests.value.filter(v => v.organization !== null && v.organization !== '').map(v => ({ label: v.organization, value: v.organization })).sort();
                setOrganizationOptions(uniqBy(requestsOrganizations, (a, b) => a.value === b.value));

                if (countries.length > 0) {
                    const requestsCountries = requests.value.map(v => ({ label: formatCountry(v.countryCode), value: v.countryCode })).sort();
                    setCountryOptions(uniqBy(requestsCountries, (a, b) => a.value === b.value));
                }
            }
            setIsFetchingFilterOptions(false);
        }

        getFilterOptions();
    }, [countries]);

    React.useEffect(() => {
        const getModalOptions = async () => {
            const studies = await client.client.getStudies();
            const mapToOptions = (results) => results.map(v => ({ label: `(${v.studyId !== null ? formatStudy(v.studyId, studies.value) : "All Studies"}) ${v.name}`, value: v.id }));

            const getAssignableRoles = (roles): Role[] => roles.filter(r => r.name !== "Device Hub Client" && r.name !== "Gateway Client");

            const fetchRoles = async () => {
                const currentUserStudyIds = currentUser.Roles.map(r => r.Study?.Id);

                if (currentUserStudyIds[0] === undefined && HasGlobalPermission(currentUserPermissions, PermissionEnum.AssignAllRoles))
                    setRoleOptions(mapToOptions(getAssignableRoles(roles)));
                else if (HasPermissionMultiStudies(currentUserPermissions, currentUserStudyIds, PermissionEnum.AssignAllRoles))
                    setRoleOptions(mapToOptions(getAssignableRoles(roles.filter(r => currentUserStudyIds.includes(r.studyId)))));
                else {
                    const currentUsersRoleChildRoles = currentUser.Roles.flatMap(r => r.ChildRolesIds);
                    const filteredRoles = getAssignableRoles(roles).filter(r => currentUsersRoleChildRoles.includes(r.id));

                    setRoleOptions(mapToOptions(filteredRoles));
                }
            }

            setIsFetchingModalOptions(true);
            await fetchRoles();
            setIsFetchingModalOptions(false);
        }

        getModalOptions();

        return () => {
            client.abortController.abort();
        }
    }, [roles]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const openAcceptanceModal = (request: AccessRequest) => {
        setSelectedRequest(request);
        setIsRequestAcceptanceModalVisible(true);
    }
    const closeAcceptanceModal = () => {
        setSelectedRequest(null);
        setIsRequestAcceptanceModalVisible(false);
    }

    const handleUserAcceptance = async (requestId: string, sitesIds: string[], rolesIds: string[]) => {
        try {
            setErrorAlert(undefined);
            setDecisionButtonsDisabled(true);
            setSelectedRequest(null);
            const request = await client.client.getAccessRequest(requestId, "email");
            const userCreation: UserCreation = { email: request.email, hasRequest: true, siteIds: sitesIds, roleIds: rolesIds, timeZoneId: request.timeZoneId, preferredLanguageCode: request.preferredLanguageCode };
            await client.client.postUser(userCreation);
            refresh();
            setIsRequestAcceptanceModalVisible(false);
            setDecisionButtonsDisabled(false);
        } catch (ex) {
            setErrorAlert(ex.message);
        }
    }

    const handleUserRejection = async (requestId: string) => {
        setDecisionButtonsDisabled(true);
        await client.client.deleteAccessRequest(requestId);
        refresh();
        setDecisionButtonsDisabled(false);
    }

    const formatCountry = (countryCode: string) => {
        return countryCode ? countries.find(c => c.code === countryCode).name : "";
    }

    const formatStudy = (studyId: string, studies: Study[]) => {
        return studies.find(s => s.id === studyId).name ?? "";
    }
    const RenderDecisionButtons = (request: AccessRequest) => {
            const content = (<>
                <button style={decisionButtonsStyles} data-automationid="request-accept" type="button" className="btn btn-success btn-xs" disabled={decisionButtonsDisabled} onClick={() => { openAcceptanceModal(request) }}>
                    Accept
                </button>
                <button style={decisionButtonsStyles} data-automationid="request-reject" type="button" className="btn btn-danger btn-xs" disabled={decisionButtonsDisabled} onClick={() => { handleUserRejection(request.id) }}>
                    Reject
                </button>
            </>);

        return (
            <div style={decisionButtonsContainerStyles}>
                {content}
            </div>
        );
    }

    if (isFetchingCountries || isFetchingFilterOptions || isFetchingModalOptions) {
        return (<LoadingWithMessage message={`Loading Requests...`} />);
    }

    return (
        <>
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ibox">
                            <div className="ibox-title">
                                <h2 className="text-primary">Requests</h2>
                                <div className="ibox-tools">
                                    <button className="btn btn-primary" onClick={clearAllFilters}>
                                        Clear All Filters
                                    </button>&nbsp;
                                    <GlobalAuthorization permissionId={PermissionEnum.VerifyUserAccess} currentPermissions={currentUserPermissions}>
                                        <button className="btn btn-primary btn-outline" onClick={() => setVerifyVisible(true)}>
                                            Upload Verification File
                                        </button>&nbsp;
                                    </GlobalAuthorization>
                                </div>
                            </div>
                            <div className="ibox-content">
                                <div id="requestsList">
                                    <Table data={data}>
                                        <Column field="email" header={<MatchSortHeader title="Email" field="email" options={emailOptions} sortField="email" />} formatter={email => email} />
                                        <Column field="fullName" header={<MatchSortHeader title="Name" field="fullName" options={nameOptions} sortField="fullName" />} formatter={fullName => fullName} />
                                        <Column field="organization" header={<MatchSortHeader title="Organization" field="organization" options={organizationOptions} sortField="organization" />} formatter={organization => organization} />
                                        <Column field="countryCode" header={<MatchSortHeader title="Country" field="countryCode" options={countryOptions} sortField="countryCode" />} formatter={countryCode => formatCountry(countryCode)} />
                                        <Column field="createdAt" header={<DateMatchSortHeader title="Created" field="createdAt" sortField="createdAt" />} formatter={occurred => formatDateTime(occurred)} />
                                        <Column field="" formatter={request => RenderDecisionButtons(request)} />
                                    </Table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <RequestAcceptanceModal
                roles={roles}
                roleOptions={roleOptions}
                show={isRequestAcceptanceModalVisible}
                request={selectedRequest}
                decisionButtonsDisabled={decisionButtonsDisabled}
                onClose={closeAcceptanceModal}
                error={errorAlert}
                onAccept={(userId: string, rolesIds: string[], sitesIds: string[]) => handleUserAcceptance(userId, rolesIds, sitesIds)} />

            {verifyVisible ? <VerifyAccessRequestsModal onClose={() => setVerifyVisible(false)} /> : null}
        </>
    );
} 
